// Timed-out-send registry — the userMessage ids whose send hit the 180s guard in this tab (#658).
//
// Why: a guard timeout never learns the WhatsApp key, so the pending-ack registry (key → id) has no
// entry and the change:ack push for that message is dropped. The durable ack channel
// (BT_MESSAGE_ACK_UPDATE, id decoded from the messageSecret) does see the late ack, but forwards only
// ack >= 2 because ack 1 is normally the confirmed send-log baseline. For a timed-out send it is not:
// the row was logged `failed`, and a group message can sit at ack 1 long after WhatsApp took it. This
// set tells that channel to forward the SERVER ack (1) for exactly these ids, so the backend
// (promoteOrRecordServerAck, BE#326) repairs the false `failed`.
//
// In-memory + bounded, like the pending-ack registry. A reload drops it; the retry store-check still
// confirms the message the next time it runs.

const MAX_ENTRIES = 5000; // FIFO-evict the oldest beyond this
/** Observed late confirmations land up to ~46 min after the failed log (prod 10-26); keep well past that. */
export const TIMED_OUT_SEND_TTL_MS = 2 * 60 * 60 * 1000;

const registry = new Map<string, number>(); // userMessage._id → expiry (epoch ms)

/** Record that this message's send timed out. Re-marking a retry's timeout refreshes the expiry. */
export function markSendTimedOut(userMessageId: string, now: number = Date.now()): void {
    if (!userMessageId) return;
    registry.delete(userMessageId); // re-insert so FIFO order follows the latest timeout
    if (registry.size >= MAX_ENTRIES) {
        const oldest = registry.keys().next().value;
        if (oldest !== undefined) registry.delete(oldest);
    }
    registry.set(userMessageId, now + TIMED_OUT_SEND_TTL_MS);
}

/** True (and removed) when this id timed out here and has not expired, so its ack is forwarded once. */
export function takeTimedOutSend(userMessageId: string, now: number = Date.now()): boolean {
    if (!userMessageId) return false;
    const expiresAt = registry.get(userMessageId);
    if (expiresAt === undefined) return false;
    registry.delete(userMessageId);
    return expiresAt > now;
}

/** Tests only. */
export function resetTimedOutSends(): void {
    registry.clear();
}
